'use strict';

// import Vue from 'vue'

// this stuff is not taken from rootGetters['situation/api/responseAPI']
// import Api from '@/api/response.js'
// --
import Vue from 'vue';
import BrowserStore from 'store';
import Axios from 'axios';
import qs from 'qs';
import router from '@/routes_new/index.js';

import { setResponseComputedName, parseResponse, handleError } from '@/_helpers/utils.js';

import tabs from './response/tabs.js';
import tags from './response/tags.js';

const state = {
  loading: true,
  error: null,
  response: null,
  publicResponse: null,
  comments: [],
  responseHistory: [],
  isLoadingResponseHistory: false,
  history: [],
  voicebot: null,
  competences: [],
  competencesAi: [],
  competencesAiUserValues: [],
  overallRating: null,
  allOverallRating: null,
  sendingMessage: false,
  sendMessageSuccess: false,
  sendMessageError: null,
  competencesDisallow: false,
  competencesAiDisallow: false,
  competencesAiUserValuesDisallow: false,
  commentsDisallow: false,
  // show/hide of some page blocks, false || undefined means block is open, true means block is hidden
  blocksHiddenState: BrowserStore.get('response:blocksHiddenState') || {},
  sendingStatusMessage: false,
  sendStatusMessageSuccess: false,
  sendStatusMessageError: null
};

const actions = {
  fetch({ commit, dispatch, rootGetters }, { id }) {
    commit('fetch');
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.fetchOne({ id })
      .then(data => {
        // determine active process
        const activeProcessName = data.meta && data.meta.active_process ? data.meta.active_process : null;

        dispatch('process/setName', activeProcessName, { root: true });
        // --

        commit('fetchSuccess', data);
      })
      .catch(err => {
        if (err.status === 404) {
          router.push({ name: 'vcv-not-found' });
        }

        dispatch('global/setError', err, { root: true });
      });
  },

  async fetchResponseHistory({ state, commit }, turnOnLoading = false) {
    if (turnOnLoading) {
      commit('SET_IS_LOADING_RESPONSE_HISTORY', true);
    }

    const params = {
      filter: { response_id: state.response.id },
      page: 1,
      limit: 9999,
      sort: { created_at: 'desc' },
      with: ['user.userName']
    };

    await Axios.get(`/v3/response-events`, {
      params: params,
      paramsSerializer: params => qs.stringify(params)
    })
      .then(response => {
        commit('SET_RESPONSE_HISTORY', response.data._embedded.events.reverse());
      })
      .finally(() => {
        commit('SET_IS_LOADING_RESPONSE_HISTORY', false);
      });
  },

  fetchCompetences({ commit, rootGetters }, id) {
    commit('fetchCompetences');
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.fetchCompetences(id)
      .then(data => commit('updateCompetences', data))
      .catch(err => {
        if (err.status === 403) {
          commit('competencesDisallow');
        } else {
          commit('updateCompetences', { err });
        }
      });
  },

  fetchCompetencesAi({ commit, rootGetters }, id) {
    commit('fetchCompetencesAi');
    return Axios.get(`/v2/responses/${id}/ai-competence/marks`)
      .then(response => {
        if (response && response.data && response.data._embedded) {
          commit('updateCompetencesAi', response.data._embedded.marks);
        }

        return response;
      })
      .catch(err => {
        if (err.status === 403) {
          commit('competencesAiDisallow');
        } else {
          commit('updateCompetencesAi', { err });
        }
      });
  },

  fetchCompetencesAiUserValues({ commit, rootGetters }, id) {
    commit('fetchCompetencesAiUserValues');
    return Axios.get(`/v2/responses/${id}/ai-competence/user-value`)
      .then(response => {
        if (response && response.data && response.data._embedded) {
          commit('updateCompetencesAiUserValues', response.data._embedded.values);
        }

        return response;
      })
      .catch(err => {
        if (err.status === 403) {
          commit('competencesAiUserValuesDisallow');
        } else {
          commit('updateCompetencesAiUserValues', { err });
        }
      });
  },

  fetchComments({ commit, dispatch, rootGetters }, id) {
    commit('fetchComments');
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.fetchComments(id)
      .then(data => commit('updateComments', data))
      .catch(err => {
        if (err.status === 403) {
          commit('commentsDisallow');
        } else {
          dispatch('global/setError', err, { root: true });
        }
      });
  },

  fetchHistory({ commit, dispatch, rootGetters }, id) {
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.fetchHistory(id)
      .then(data => commit('updateHistory', data))
      .catch(err => {
        dispatch('global/setError', err, { root: true });
      });
  },

  fetchVoicebot({ commit, dispatch, rootGetters }, id) {
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.fetchVoicebot(id)
      .then(data => commit('updateVoicebot', data))
      .catch(err => dispatch('global/setError', err, { root: true }));
  },

  updateCompetences({ commit, dispatch, rootGetters }, { id, competences }) {
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.updateCompetences(id, competences)
      .then(data => commit('updateCompetences', data))
      .catch(err => dispatch('global/setError', err, { root: true }));
  },

  updateCompetencesAiUserValues({ commit, dispatch, rootGetters }, { id, competences }) {
    return Axios.put(`/v2/responses/${id}/ai-competence/user-value`, competences)
      .then(response => {
        if (response && response.data && response.data._embedded) {
          commit('updateCompetencesAiUserValues', response.data._embedded.values);
        }
        return response;
      })
      .catch(err => {
        return Promise.reject(err);
      });
  },

  deleteCompetences({ commit, dispatch, rootGetters }, { id, competences }) {
    return Axios.delete(`/1.0/responses/${id}/competences`, { data: { competences } })
      .then(response => {
        const data = parseResponse(response).data[0];
        commit('updateCompetences', data);
      })
      .catch(err => {
        const error = handleError(err);
        dispatch('global/setError', error, { root: true });
      });
  },

  createComment({ commit, dispatch, rootGetters }, { id, message }) {
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.createComment(id, message)
      .then(() => Api.fetchComments(id))
      .then(data => {
        commit('updateComments', data);

        return data;
      })
      .catch(err => dispatch('global/setError', err, { root: true }));
  },

  updateComment({ commit, dispatch, rootGetters }, { responseId, commentId, message }) {
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.updateComment(responseId, commentId, message)
      .then(() => Api.fetchComments(responseId))
      .then(data => {
        commit('updateComments', data);

        return data;
      })
      .catch(err => dispatch('global/setError', err, { root: true }));
  },

  deleteComment({ commit, dispatch, rootGetters }, { responseId, commentId }) {
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.deleteComment(responseId, commentId)
      .then(() => Api.fetchComments(responseId))
      .then(data => commit('updateComments', data))
      .catch(err => dispatch('global/setError', err, { root: true }));
  },

  save({ commit, dispatch, rootGetters }, model) {
    commit('save');
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.updateOne(model)
      .then(data => commit('fetchSuccess', data))
      .then(() => dispatch('company_new/get_rates', null, { root: true }))
      .catch(err => dispatch('global/setError', err, { root: true }));
  },

  saveQuietly({ commit, dispatch, rootGetters }, model) {
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.updateOne(model)
      .then(data => commit('fetchSuccess', data))
      .then(() => dispatch('company_new/get_rates', null, { root: true }))
      .catch(err => dispatch('global/setError', err, { root: true }));
  },

  saveQuietlyList({ commit, dispatch, rootGetters }, model) {
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.updateList(model)
      .then(data => commit('fetchSuccess', data))
      .then(() => dispatch('company_new/get_rates', null, { root: true }))
      .catch(err => dispatch('global/setError', err, { root: true }));
  },

  setStar({ commit, dispatch, rootGetters }, { model, count }) {
    commit('updateStarRating', { model, count });
    const Api = rootGetters['situation/api/responseAPI'];
    return Api.updateStar(model.id, count).catch(err =>
      dispatch('global/setError', err, { root: true })
    );
  },

  delete_star({ commit, dispatch, rootGetters }, { model }) {
    return Axios.delete(`/1.0/responses/${model.id}/stars`)
      .then(response => {
        commit('updateStarRating', { model, count: null });
        return parseResponse(response).data[0];
      })
      .catch(err => {
        const error = handleError(err);
        dispatch('global/setError', error, { root: true });
      });
  },

  toggleBlock({ commit }, blockId) {
    commit('toggleBlock', blockId);
  },

  add_tag(context, { tag, response_id }) {
    return Axios.post(`/1.0/response/tags/${tag.id}/add`, { response_id })
      .then(() => {
        context.commit('add_tag', {
          id: tag.id,
          name: tag.name
        });
      })
      .catch(error => {
        error = handleError(error);
        throw error;
      });
  },

  remove_tag(context, { tag_id, response_id }) {
    return Axios.post(`/1.0/response/tags/${tag_id}/remove`, { response_id })
      .then(() => {
        context.commit('remove_tag', tag_id);
      })
      .catch(error => {
        error = handleError(error);
        throw error;
      });
  }
};

const mutations = {
  fetch(state) {
    state.response = null;
    state.loading = true;
  },

  fetchSuccess(state, data) {
    state.response = setResponseComputedName(data);
    state.loading = false;
  },

  fetchFailure(state, err) {
    state.error = err;
    state.loading = false;
  },

  sendMessage(state) {
    state.sendMessageError = null;
    state.sendingMessage = true;
  },

  sendMessageSuccess(state, data) {
    state.sendingMessage = false;
    state.sendMessageSuccess = true;
  },

  sendMessageFailure(state, err) {
    state.sendingMessage = false;
    state.sendMessageError = err;
  },

  sendStatusMessage(state) {
    state.sendStatusMessageError = null;
    state.sendingStatusMessage = true;
  },

  sendStatusMessageSuccess(state, data) {
    state.sendingStatusMessage = false;
    state.sendStatusMessageSuccess = true;
  },

  sendStatusMessageFailure(state, err) {
    state.sendingStatusMessage = false;
    state.sendStatusMessageError = err;
  },

  update(state, { model, data }) {
    Object.assign(model, data);
  },

  fetchCompetences(state) {
    state.competencesDisallow = false;
  },

  fetchCompetencesAi(state) {
    state.competencesAiDisallow = false;
  },

  fetchCompetencesAiUserValues(state) {
    state.competencesAiUserValuesDisallow = false;
  },

  competencesDisallow(state) {
    state.competencesDisallow = true;
  },

  competencesAiDisallow(state) {
    state.competencesAiDisallow = true;
  },

  competencesAiUserValuesDisallow(state) {
    state.competencesAiUserValuesDisallow = true;
  },

  updateCompetences(state, data) {
    state.competences = data.competences
      ? data.competences.sort((a, b) => (a.sort_order < b.sort_order ? -1 : 1))
      : [];
    state.overallRating = data.overall_rating || null;
    state.allOverallRating = data.all_overall_rating || null;
  },

  updateCompetencesAi(state, data) {
    let marks = [];
    marks = data.map(item => {
      if (item._embedded && item._embedded.competence) {
        return Object.assign({}, item, item._embedded.competence);
      } else {
        return item;
      }
    });
    state.competencesAi = marks;
  },

  updateCompetencesAiUserValues(state, data) {
    let marks = [];
    marks = data.map(item => {
      if (item._embedded && item._embedded.competence) {
        return Object.assign({}, item, item._embedded.competence);
      } else {
        return item;
      }
    });
    state.competencesAiUserValues = marks;
  },

  updateComments(state, data) {
    state.comments = data;
  },

  updateHistory(state, data) {
    state.history = data;
  },

  fetchComments(state) {
    state.commentsDisallow = false;
  },

  commentsDisallow(state) {
    state.commentsDisallow = true;
  },

  updateVoicebot(state, data) {
    state.voicebot = data;
  },

  save(state) {
    state.loading = true;
  },

  setPublicResponse(state, data) {
    state.publicResponse = data;
  },

  updateStarRating(state, { model, count }) {
    state.response.star = { value: count };
  },

  add_tag(state, tag) {
    Vue.set(state.response, 'tags', [...state.response.tags, tag]);
  },

  remove_tag(state, id) {
    const tags = state.response.tags.slice();
    const position = tags.map(tag => tag.id).indexOf(id);
    if (~position) {
      tags.splice(position, 1);
      Vue.set(state.response, 'tags', tags);
    }
  },

  edit_tag(state, { id, name }) {
    const position = state.response.tags.map(tag => tag.id).indexOf(id);
    if (~position) {
      Vue.set(state.response.tags[position], 'name', name);
    }
  },

  /**
   *
   * @param {*} state
   * @param {string} blockId
   */
  toggleBlock(state, blockId) {
    state.blocksHiddenState[blockId] = !state.blocksHiddenState[blockId];
    BrowserStore.set('response:blocksHiddenState', state.blocksHiddenState);
  },

  SET_RESPONSE_HISTORY: (state, payload) => {
    state.responseHistory = payload;
  },

  SET_IS_LOADING_RESPONSE_HISTORY: (state, payload) => {
    state.isLoadingResponseHistory = payload;
  }
};

const getters = {
  error(state, getters) {
    return state.error;
  },

  loading(state, getters) {
    return state.loading;
  },

  one(state, getters) {
    return state.response || { name: '', modules: [] };
  },

  publicResponse(state, getters) {
    return state.publicResponse;
  },

  competences(state, getters) {
    return state.competences;
  },

  competencesAi(state, getters) {
    return state.competencesAi;
  },

  competencesAiUserValues(state, getters) {
    return state.competencesAiUserValues;
  },

  overallRating(state, getters) {
    return state.overallRating;
  },

  allOverallRating(state, getters) {
    return state.allOverallRating;
  },

  comments(state, getters) {
    return state.comments;
  },

  history(state, getters) {
    return state.history;
  },

  voicebot(state, getters) {
    return state.voicebot;
  },

  candidate(state, getters) {
    return getters['one'].candidate;
  },

  blockHidden(state) {
    return state.blocksHiddenState;
  },

  process: state => (state.response && state.response.meta ? state.response.meta.active_process : null),

  tags: state => (state.response && state.response.tags ? state.response.tags : []),

  talentq: state => (state.response && state.response.talentq ? state.response.talentq : null),

  response_id: (state, getters, rootState) => rootState.route.params.id
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
  modules: {
    tabs,
    tags
  }
};
